import React, { useState } from 'react'
import { Collapse } from 'react-bootstrap'
import {
  useGetAgendaQuery,
  useDeleteAgendaItemMutation,
  useCreateAgendaItemMutation,
  useUpdateAgendaItemMutation,
  useReorderAgendaItemsMutation,
} from '../../../../../store/agenda/agenda'
import UpcomingMeetingView from './UpcomingMeetingView'
import { Button } from '../../../../../components/Button/Button'
import { addToast } from '../../../../../store/toast/toastSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import * as Sentry from '@sentry/react'
import classNames from 'classnames'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { LiveMeetingView } from './LiveMeetingView'
import { AgendaItem, AgendaItemStatus, AgendaItemSource, AgendaItemStatusChangedBy } from '../../../../../models/Agenda'
import { useTracking, TrackEvent } from '../../common/tracking'
import { isLiveSession } from '../../../../../common/session.util'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { parseMetadata } from '../../../../../common/util'
import { useEffectiveConfig } from '../../../../../common/useEffectiveConfig'
import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../../../common/copy'

interface AgendaProps {
  accountId: string
  roomId: string
  sessionId: string
  meetingTemplateId: string
}

const AGENDA_POLLING_INTERVAL = 3 * 60000

export const Agenda: React.FC<AgendaProps> = ({ accountId, roomId, sessionId, meetingTemplateId }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()
  const { hasQuota } = useZocksQuota()
  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const roomSession = useAppSelector(selectRoomSession)

  const { source = 'voice-memo' } = parseMetadata(roomSession?.metadata)
  const isInPersonMeeting = source === 'voice-memo'

  const hasLiveAiFeaturesQuota = hasQuota('ai.liveAiFeatures.value')
  const isLive = !!roomSession && isLiveSession(roomSession)
  const shouldPoll = isLive && hasLiveAiFeaturesQuota && !isInPersonMeeting

  const {
    data: agenda,
    isFetching,
    isError,
    startedTimeStamp: pollingTimeStamp,
  } = useGetAgendaQuery(
    { accountId, roomId, sessionId, meetingTemplateId },
    { skip: !accountId || !roomId || !sessionId || !meetingTemplateId, pollingInterval: shouldPoll ? AGENDA_POLLING_INTERVAL : 0 },
  )

  const [deleteAgendaItem] = useDeleteAgendaItemMutation()
  const [createAgendaItem] = useCreateAgendaItemMutation()
  const [updateAgendaItem] = useUpdateAgendaItemMutation()
  const [reorderAgendaItems] = useReorderAgendaItemsMutation()

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)

  const agendaItems = agenda?.items ?? []

  const handleCopyAll = () => {
    if (!agendaItems || agendaItems.length === 0 || accountIsFetching) {
      return
    }

    const textContent = agendaItems.map((item) => `${item.text} - ${item.status.toUpperCase()}`).join('\n')
    const htmlContent = agendaItems.map((item) => `<p><strong>${item.text}</strong> - ${item.status.toUpperCase()}</p>`).join('\n')

    const _copyContent = { [TEXT_TYPE]: textContent, [HTML_TYPE]: htmlContent }
    const copyContent = extendCopyContentWithComplianceMessage(_copyContent, copyComplianceMessage)

    writeCopyContentToClipboard(copyContent)
      .then(() => {
        dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['All agenda items copied to clipboard'] }))
      })
      .catch((err) => {
        Sentry.captureException(err)
        console.error('Failed to copy to clipboard:', err)
        dispatch(addToast({ variant: 'error', title: 'Copy failed', messages: ['Failed to copy agenda items to clipboard'] }))
      })
  }

  const _handleAdd = (text: string) => {
    if (!agenda?.id || !text.trim()) {
      return
    }
    const existingOrderingIndexes = agendaItems.map((item) => item.orderingIndex)
    const newOrderingIndex = existingOrderingIndexes.length > 0 ? Math.max(...existingOrderingIndexes) + 1 : 0

    createAgendaItem({
      accountId,
      agendaId: agenda.id,
      item: {
        text: text.trim(),
        orderingIndex: newOrderingIndex,
        source: AgendaItemSource.User,
      },
    })
  }

  const handleAdd = tracked(_handleAdd, {
    eventName: TrackEvent.AGENDA_ITEM_ADDED,
    eventDetails: {
      agendaId: agenda?.id ?? '',
      roomId,
      sessionId,
      meetingTemplateId,
    },
  })

  const _handleDelete = (agendaItemId: string) => {
    if (!agenda?.id) {
      return
    }

    deleteAgendaItem({
      accountId,
      agendaId: agenda.id,
      agendaItemId,
      roomId,
      sessionId,
      meetingTemplateId,
    })
  }

  const handleDelete = tracked(_handleDelete, {
    eventName: TrackEvent.AGENDA_ITEM_DELETED,
    eventDetails: {
      agendaId: agenda?.id ?? '',
      roomId,
      sessionId,
      meetingTemplateId,
    },
  })

  const handleEdit = (agendaItemId: string) => {
    setEditingItemId(agendaItemId)
  }

  const handleCancelEdit = () => {
    setEditingItemId(null)
  }

  const _handleAgendaItemTextUpdate = (agendaItemId: string, text: string) => {
    if (!agenda?.id || !text.trim()) {
      return
    }

    updateAgendaItem({
      accountId,
      agendaId: agenda.id,
      agendaItemId,
      updatePayload: {
        id: agendaItemId,
        text: text.trim(),
        source: AgendaItemSource.User,
      },
    })
    setEditingItemId(null)
  }

  const handleAgendaItemTextUpdate = tracked(_handleAgendaItemTextUpdate, {
    eventName: TrackEvent.AGENDA_ITEM_TEXT_UPDATED,
    eventDetails: {
      agendaId: agenda?.id ?? '',
      roomId,
      sessionId,
      meetingTemplateId,
    },
  })

  const _handleReorder = (reorderedItems: AgendaItem[]) => {
    if (!agenda?.id) {
      return
    }
    reorderAgendaItems({
      accountId,
      agendaId: agenda.id,
      items: reorderedItems.map((item, index) => ({ itemId: item.id, orderingIndex: index })),
    })
  }

  const handleReorder = tracked(_handleReorder, {
    eventName: TrackEvent.AGENDA_ITEMS_REORDERED,
    eventDetails: { agendaId: agenda?.id ?? '', roomId, sessionId, meetingTemplateId },
  })

  const _handleToggleComplete = (agendaItemId: string, newStatus: AgendaItemStatus) => {
    if (!agenda?.id) {
      return
    }

    updateAgendaItem({
      accountId,
      agendaId: agenda.id,
      agendaItemId,
      updatePayload: {
        id: agendaItemId,
        status: newStatus,
        status_changed_by: AgendaItemStatusChangedBy.User,
      },
    })
  }

  const handleToggleComplete = tracked(
    _handleToggleComplete,
    {
      eventName: TrackEvent.AGENDA_ITEM_STATUS_UPDATED,
      eventDetails: {
        agendaId: agenda?.id ?? '',
        roomId,
        sessionId,
        meetingTemplateId,
      },
    },
    {
      eventDetailsExtractor: (_agendaItemId: string, newStatus: AgendaItemStatus) => ({
        status: newStatus,
      }),
    },
  )

  if (!roomSession) {
    return null
  }

  if (isError) {
    return null
  }

  if (!agenda) {
    return null
  }

  const hasItems = agendaItems?.length > 0

  return (
    <div className='border zocks-border-0.5 bg-white zocks-border-gray-200'>
      <div className='d-flex justify-content-between align-items-center zocks-py-1.25 zocks-px-1.75 w-100'>
        <h5 className='mb-0'>Agenda</h5>
        <div className='d-flex align-items-center zocks-gap-0.75'>
          {hasItems && (
            <Button className='button-small zocks-px-0.75 zocks-py-0.5' variant='header' onClick={handleCopyAll} disabled={isFetching}>
              <i className='bi bi-copy' />
              <span className='ms-1'>Copy all</span>
            </Button>
          )}
          <i
            className={classNames('bi', {
              'bi-chevron-expand': isCollapsed,
              'bi-chevron-contract': !isCollapsed,
            })}
            onClick={() => setIsCollapsed((v) => !v)}
          />
        </div>
      </div>
      <Collapse in={!isCollapsed} dimension={'height'}>
        <div>
          <div className='zocks-px-1.75 zocks-pb-1.25'>
            {isLive ? (
              <LiveMeetingView items={agendaItems} onToggleComplete={handleToggleComplete} isPollingActive={shouldPoll} pollingTimeStamp={pollingTimeStamp} />
            ) : (
              <UpcomingMeetingView
                items={agendaItems}
                meetingTemplateId={meetingTemplateId}
                onDelete={handleDelete}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onUpdate={handleAgendaItemTextUpdate}
                onCancelEdit={handleCancelEdit}
                editingItemId={editingItemId}
                onReorder={handleReorder}
              />
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}
